import _ from "lodash";
import { memo, useCallback, useEffect, useState } from "react";
import TemporaryDrawer from "../ui/temp-drawer.component";
import { disableBodyScroll, enableBodyScroll } from "../../utils/HtmlHelper";
import { PowerUpItem } from "../../reducers/rush-game.reducer";
import { CTAPowerUp, CondensedPowerUp } from "./power-up-items.component";
import { useBuyProductWithGemsMutation, useGetProductsQuery } from "../../apis/shop.api";
import { useGetInventoryQuery } from "../../apis/users.api";
import { GroupInventoryItem, InventoryItem } from "../../models/User";
import { toastCustomError, toastError, toastSuccess } from "../../utils/ToastUtils";
import { ShopProduct } from "../../models/shop/Product";
import { useNavigate } from "react-router-dom";

const EmptyPowerUp: React.FC = () => {
  return (
    <div className="border-2 border-gray-700 border-dashed rounded-2xl flex flex-1 center gap-2">
      <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M9 0C9.45087 0 9.88327 0.179107 10.2021 0.497919C10.5209 0.816731 10.7 1.24913 10.7 1.7V6.8H15.8C16.2509 6.8 16.6833 6.97911 17.0021 7.29792C17.3209 7.61673 17.5 8.04913 17.5 8.5C17.5 8.95087 17.3209 9.38327 17.0021 9.70208C16.6833 10.0209 16.2509 10.2 15.8 10.2H10.7V15.3C10.7 15.7509 10.5209 16.1833 10.2021 16.5021C9.88327 16.8209 9.45087 17 9 17C8.54913 17 8.11673 16.8209 7.79792 16.5021C7.47911 16.1833 7.3 15.7509 7.3 15.3V10.2H2.2C1.74913 10.2 1.31673 10.0209 0.997919 9.70208C0.679107 9.38327 0.5 8.95087 0.5 8.5C0.5 8.04913 0.679107 7.61673 0.997919 7.29792C1.31673 6.97911 1.74913 6.8 2.2 6.8H7.3V1.7C7.3 1.24913 7.47911 0.816731 7.79792 0.497919C8.11673 0.179107 8.54913 0 9 0V0Z" fill="#3F3F46"/>
      </svg>
      Power-Up
    </div>
  );
}

interface PowerUpSelectorProps {
  isPlaying: boolean,
  powerUp: GroupInventoryItem,
  setPowerUp: (powerUp: GroupInventoryItem) => void,
  resetPowerUp: () => void
}

interface InventoryQuantityByCode {
  code: string,
  quantity: number,
}

// Utility - Get quantity of grouped power-ups by code
const groupInventoryByCode = (inventory: InventoryItem[]):InventoryQuantityByCode[] => {

  if(!inventory) {
    return []
  };

  const groupedByCode = _.groupBy(inventory, 'code');
  const quantityByCode = Object.entries(groupedByCode).map((entry) => {
    return {
      code: entry[0],
      quantity: entry[1].length
    }
  });

  console.log("quantityByCode: ", quantityByCode);

  return quantityByCode;
}

export const PowerUpSelector = memo<PowerUpSelectorProps>(({isPlaying, powerUp, setPowerUp, resetPowerUp}) => {

  const navigate = useNavigate();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [userInventory, setUserInventory] = useState([] as InventoryQuantityByCode[]);

  const [buyProductWithGems] = useBuyProductWithGemsMutation();
  const {data: shieldShopProducts, isLoading: areShieldShopProductsLoading} = useGetProductsQuery({family: 'gems_buyable_items', subFamily: 'shield'});

  const {data: inventory, isLoading: isInventoryLoading } = useGetInventoryQuery({});

  useEffect(() => {

    console.log('inventory: ', inventory);

    if(inventory) {
      setUserInventory(groupInventoryByCode(inventory));
    }

  }, [inventory, isInventoryLoading])

  useEffect(() => {

    console.log('shieldShopProducts: ', shieldShopProducts);

  }, [areShieldShopProductsLoading])

  const handleSetClick = useCallback((powerUp: GroupInventoryItem) => {
    setPowerUp(powerUp);
    setOpenDrawer(false);
  }, [setPowerUp]);

  const handleResetClick = useCallback( () => {
    resetPowerUp();
  }, [resetPowerUp]);

  const handleBuyInventoryItem = useCallback(async(powerUp: ShopProduct) => {

    await buyProductWithGems({ productCode: powerUp.code })
    .then((data: any) => {

      if (data.error) {
        toastError(data.error.data.message || 'Unknown error');
        return;
      }

      setPowerUp({...powerUp, quantity:1});

      gtag('event', 'buyShopItemWithGems', {
        inventory_item_code: powerUp.code,
        inventory_item_price: powerUp.displayedPrice
      });

    });

  }, []);

  const isUsed = (code:string):boolean => {

    if(!powerUp || !powerUp?.code) {
      return false;
    }

    console.log('code: ', code);
    console.log('powerUp.code: ', powerUp.code);
    if(!powerUp) {
      return false;
    } else if(powerUp && powerUp.code === code) {
      return true;
    } else {
      return false;
    }

  }

  const getInventoryQuantity = (code:string):number => {

    console.log('getInventoryQuantity for code: ', code);

    if (!userInventory) {
      return 0;
    }

    const item = userInventory.find((item) => item.code === code);

    if (!item) {
      return 0;
    } else {
      return item.quantity;
    }

  }

  return(
    <TemporaryDrawer
      title={'Power-Ups'}
      anchor={'bottom'}
      size={'auto'}
      className={'w-full h-full'}
      buttonClass={"w-full h-full"}
      element={
        <div className="w-full h-full flex" onClick={() => setOpenDrawer(true)}>
          { (powerUp.code === '') && <EmptyPowerUp/> }
          { (powerUp.code !== '') && <CondensedPowerUp powerUp={powerUp}/> }
        </div>
      }
      open={openDrawer}
      onOpen={() => {
        disableBodyScroll();
        setOpenDrawer(true);
      }}
      onClose={() => {
        enableBodyScroll();
        setOpenDrawer(false)
      }}
    >

      <div className={"w-full h-full overflow-y-auto pb-8"}>

        <h3 className="text-2xl center text-center">Use a shield to reduce losses</h3>

        <div className="w-3/4 mx-auto">

          { isPlaying &&
            <div className="flex center w-full my-5">
              <p>
                Your trade is not closed yet...
              </p>
            </div>
          }

          {/* Amount values */}
          { !isPlaying && shieldShopProducts &&
            <div className="grid grid-col gap-y-6 my-6">

              { shieldShopProducts.map((item, index) => (

                <div key={index} className="w-full center mx-auto"
                  onClick={() => {

                    const itemQuantity = getInventoryQuantity(item.code);
                    const isItemUsed = isUsed(item.code);

                    if(itemQuantity > 0 && !isItemUsed) {
                      {/* START USE */ }
                      handleSetClick({...item, quantity: itemQuantity});
                    } else if (itemQuantity > 0 && isItemUsed) {
                      {/* STOP USE */ }
                      handleResetClick();
                    } else {
                      handleBuyInventoryItem(item);
                      // If enough gems >> Buy 1 and use it
                      // else toast error
                      // toastCustomError('Not enough gems');
                      // toastCustomError('Buy a power-up in the shop to use it');
                    }

                  }}
                >

                  {/* TODO: CTA POWER UPS with power-ups items values */}
                  <CTAPowerUp
                    code={item.code}
                    quantity={getInventoryQuantity(item.code)}
                    isUsed={isUsed(item.code)}
                    price={item.displayedPrice}
                    value={Number(item.metadata?.value)}
                  />

                </div>

              )) }
            </div>
          }

        </div>

        <div className="w-3/4 max-w-md mx-auto">
          <button className="primary w-full"
            onClick={() => {
              navigate('/store')
            }}
          >
            Go to the shop
          </button>
        </div>

      </div>

    </TemporaryDrawer>
  )
})
